import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import "./CustomAccordion.scss";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from "@mui/material";
import { Colors } from "../../styles/constant";
import { defaultEmptyFun } from "../../utils/helper";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import CustomCircularLoader from "../CustomCircularLoader/CustomCircularLoader";

interface Props {
  title: string;
  note?: string;
  preIcon?: React.ReactNode | null;
  children?: React.ReactNode | null;
  subTitle?: string;
  handleAccordionClick?: Function;
  customClass?: string;
  expandedByDefault?: boolean;
  nodeTitle?: React.ReactNode;
  withCustom?: boolean;
  expandedIcon?: React.ReactNode | null;
  notExpandedIcon?: React.ReactNode | null;
  isPersonalLoanAccordionEnabled?: boolean;
  isCircularLoaderEnabled?: boolean;
}

const CustomAccordion: FC<Props> = ({
  title = "",
  note = "",
  preIcon,
  children,
  subTitle = "",
  handleAccordionClick = defaultEmptyFun,
  customClass = "",
  expandedByDefault = false,
  nodeTitle = undefined,
  withCustom = false,
  expandedIcon,
  notExpandedIcon,
  isPersonalLoanAccordionEnabled = false,
  isCircularLoaderEnabled,
}: Props) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const isAccordionClickCalled = useRef<boolean>(false);

  useEffect(() => {
    if (expandedByDefault && !isAccordionClickCalled.current) {
      setExpanded(expandedByDefault);
      handleAccordionClick();
      isAccordionClickCalled.current = true;
    }
  }, [expandedByDefault]);

  const handleChange = useCallback(() => {
    if (isPersonalLoanAccordionEnabled) {
      handleAccordionClick();
    } else {
      setExpanded((prevExpanded) => !prevExpanded);
      if (!expanded) {
        handleAccordionClick();
      }
    }
  }, [handleAccordionClick, withCustom, title, expanded]);

  return (
    <Box className={`custom-accordion-wrapper ${customClass}`}>
      {note && (
        <Box className="accordion-note">
          <Typography color={Colors.darkYellow} variant="h6">
            {note}
          </Typography>
        </Box>
      )}
      <Accordion
        className="custom-accordion"
        disableGutters={true}
        expanded={isPersonalLoanAccordionEnabled ? false : expanded}
        onChange={handleChange}
        variant="outlined"
      >
        <AccordionSummary
          expandIcon={
            isCircularLoaderEnabled ? (
              <CustomCircularLoader size={24} />
            ) : withCustom ? (
              expanded ? (
                expandedIcon
              ) : (
                notExpandedIcon
              )
            ) : isPersonalLoanAccordionEnabled ? (
              <KeyboardArrowRightIcon sx={{ color: Colors.red }} />
            ) : (
              <KeyboardArrowDownIcon sx={{ color: Colors.red }} />
            )
          }
          aria-controls="panel1-content"
          id="panel1-header"
          className="accordion-summary"
        >
          <Box display="flex" alignItems="center">
            {preIcon}
            <Box marginLeft={preIcon ? "0.5rem" : 0}>
              {nodeTitle ? (
                <Box>{nodeTitle}</Box>
              ) : (
                <Typography variant="h3">{title}</Typography>
              )}
              {subTitle && (
                <Typography fontWeight={500} variant="h5">
                  {subTitle}
                </Typography>
              )}
            </Box>
          </Box>
        </AccordionSummary>
        {children && (
          <AccordionDetails className="accordion-details" key={title}>
            {children}
          </AccordionDetails>
        )}
      </Accordion>
    </Box>
  );
};

export default CustomAccordion;
